import type { IAggregatedLibraryDescriptor } from "./AggregatedDocumentsService";

/**
 * Pure source-identity logic for the aggregate grid's descriptors.
 *
 * A source used to be exactly one SharePoint library, so `listId` doubled as the
 * cursor identity everywhere. Tess broke that: its dossiers can point at a FOLDER
 * inside a library (`rootFolderServerRelativeUrl`), and two dossiers may target the
 * same list — so cursor/buffer identity is now the SOURCE KEY (list + folder scope),
 * while schema, bindings, list settings and realtime enlistment stay keyed on the
 * list id (they are per-list facts).
 *
 * Kept dependency-free (the descriptor import is types-only at runtime) so unit
 * tests need none of the service's auth/axios chain.
 */

/**
 * Canonical form of a folder scope for IDENTITY comparisons only — requests always
 * use the descriptor's original path. Decoded FileRef-style server-relative input;
 * lower-cased because SharePoint URLs are case-insensitive (see project rule on
 * identity comparisons).
 */
export const normalizeAggregatedFolderPath = (path?: string): string => {
    let value = String(path ?? "").trim();
    if (!value) {
        return "";
    }
    value = value.replace(/\/+$/, "");
    if (!value) {
        return "";
    }
    if (!value.startsWith("/")) {
        value = `/${value}`;
    }
    return value.toLowerCase();
};

/**
 * The cursor/buffer identity of a source. Whole-library sources keep the bare list
 * id (byte-identical to the pre-Tess cursor keys, so nothing site-shaped changes);
 * folder-scoped sources append the normalized folder path.
 */
export const getAggregatedSourceKey = (library: Pick<IAggregatedLibraryDescriptor, "listId" | "rootFolderServerRelativeUrl">): string => {
    const folder = normalizeAggregatedFolderPath(library?.rootFolderServerRelativeUrl);
    return folder ? `${library.listId}::${folder}` : library?.listId;
};

/**
 * Drops sources whose rows another source already returns — REQUIRED before fetching:
 * page-level dedupe (`applyPage`) is per cursor, and the merge's row-key tiebreak
 * assumes a document appears in at most one cursor, so overlapping sources would
 * render duplicate rows.
 *
 *   - duplicate whole-library sources: first one wins;
 *   - a folder source inside an aggregated whole library: covered, dropped;
 *   - folder sources on one list: exact duplicates and descendants of another folder
 *     source are dropped (the shallowest scope wins).
 *
 * Prefix checks are segment-safe (`/a/b` covers `/a/b/c`, never `/a/bc`) and input
 * order is preserved so downstream identity keys stay stable.
 */
export const dedupeAggregatedSourceDescriptors = (descriptors: IAggregatedLibraryDescriptor[]): IAggregatedLibraryDescriptor[] => {
    const list = (descriptors ?? []).filter(descriptor => descriptor?.listId);

    const wholeLibraryListIds = new Set(
        list.filter(descriptor => !normalizeAggregatedFolderPath(descriptor.rootFolderServerRelativeUrl))
            .map(descriptor => String(descriptor.listId).toLowerCase()));

    const kept = new Set<IAggregatedLibraryDescriptor>();

    const seenLibraryIds = new Set<string>();
    for (const descriptor of list) {
        if (normalizeAggregatedFolderPath(descriptor.rootFolderServerRelativeUrl)) {
            continue;
        }
        const listId = String(descriptor.listId).toLowerCase();
        if (!seenLibraryIds.has(listId)) {
            seenLibraryIds.add(listId);
            kept.add(descriptor);
        }
    }

    const folderGroups = new Map<string, IAggregatedLibraryDescriptor[]>();
    for (const descriptor of list) {
        if (!normalizeAggregatedFolderPath(descriptor.rootFolderServerRelativeUrl)) {
            continue;
        }
        const listId = String(descriptor.listId).toLowerCase();
        if (wholeLibraryListIds.has(listId)) {
            continue;
        }
        const group = folderGroups.get(listId) ?? [];
        group.push(descriptor);
        folderGroups.set(listId, group);
    }

    folderGroups.forEach(group => {
        // Shallowest-first so an ancestor is always decided before its descendants;
        // the sort is on a copy, and ties keep input order (Array.sort is stable).
        const ordered = group.slice().sort((a, b) =>
            normalizeAggregatedFolderPath(a.rootFolderServerRelativeUrl).length
            - normalizeAggregatedFolderPath(b.rootFolderServerRelativeUrl).length);
        const keptPaths: string[] = [];
        for (const descriptor of ordered) {
            const path = normalizeAggregatedFolderPath(descriptor.rootFolderServerRelativeUrl);
            const isCovered = keptPaths.some(keptPath => path === keptPath || path.startsWith(`${keptPath}/`));
            if (!isCovered) {
                keptPaths.push(path);
                kept.add(descriptor);
            }
        }
    });

    return list.filter(descriptor => kept.has(descriptor));
};
